import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TaskCard = ({ card, onClick, onCardMove, onCardReorder, members, canDrag = true }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: { id: card.id, columnId: card.columnId, index: card.position, sourceColumnId: card.columnId },
    canDrag: canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      // If we dropped on a column or another card
      if (item && onCardMove) {
        // Find the final column and index from the item (which was mutated by hover)
        console.log(`🎯 Drag ended for ${item.id}. Final Column: ${item.columnId}, Final Index: ${item.index}`);
        onCardMove(item.id, item.sourceColumnId, item.columnId, item.index);
      }
    }
  });

  const [, drop] = useDrop({
    accept: 'card',
    hover(item, monitor) {
      if (!ref.current || !onCardReorder) return;

      const dragId = item.id;
      const hoverId = card.id;
      const sourceColumnId = item.columnId;
      const targetColumnId = card.columnId;
      const hoverIndex = card.position;

      // Don't replace items with themselves
      if (dragId === hoverId) return;

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      
      // If moving within the same column
      if (sourceColumnId === targetColumnId) {
        const dragIndex = item.index;
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      }

      // OPTIMISTIC MOVE: Only update local state for visual feedback
      onCardReorder(dragId, sourceColumnId, targetColumnId, hoverIndex);

      // Mutate the item to track where it is now during the drag
      item.index = hoverIndex;
      item.columnId = targetColumnId;
    },
    // Adding collect to return drop result to useDrag.end if needed
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Combine drag and drop refs
  drag(drop(ref));

  const getAssignedMembers = () => {
    // Handle both new assignments format and legacy assignedTo format
    if (card.assignments && Array.isArray(card.assignments)) {
      return card.assignments.map(assignment => {
        // Find member by user_id from assignment
        const member = members.find(member => member.id === assignment.user_id);
        return member;
      }).filter(Boolean);
    }

    // Fallback to legacy assignedTo format
    return card.assignedTo?.map(memberId =>
      members.find(member => member.id === memberId)
    ).filter(Boolean) || [];
  };

  const getPriorityConfig = () => {
    const configs = {
      high: { 
        color: 'text-red-600 bg-red-50 border-red-200', 
        border: 'border-l-red-500',
        icon: 'AlertTriangle',
        bg: 'bg-red-500'
      },
      urgent: { 
        color: 'text-rose-600 bg-rose-50 border-rose-200', 
        border: 'border-l-rose-600',
        icon: 'Zap',
        bg: 'bg-rose-600'
      },
      medium: { 
        color: 'text-amber-600 bg-amber-50 border-amber-200', 
        border: 'border-l-amber-500',
        icon: 'Clock',
        bg: 'bg-amber-500'
      },
      low: { 
        color: 'text-green-600 bg-green-50 border-green-200', 
        border: 'border-l-green-500',
        icon: 'ArrowDown',
        bg: 'bg-green-500'
      }
    };
    return configs[card.priority] || { 
      color: 'text-slate-600 bg-slate-50 border-slate-200', 
      border: 'border-l-slate-400',
      icon: 'Minus',
      bg: 'bg-slate-400'
    };
  };

  const formatDueDate = (date) => {
    if (!date) return null;
    const dueDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { text: 'Overdue', color: 'text-red-600 bg-red-100 border border-red-200', icon: 'AlertOctagon' };
    if (diffDays === 0) return { text: 'Due today', color: 'text-amber-600 bg-amber-100 border border-amber-200', icon: 'Clock' };
    if (diffDays === 1) return { text: 'Tomorrow', color: 'text-blue-600 bg-blue-100 border border-blue-200', icon: 'Calendar' };
    if (diffDays <= 7) return { text: `${diffDays} days`, color: 'text-slate-600 bg-slate-100 border border-slate-200', icon: 'Calendar' };
    return { text: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: 'text-slate-500 bg-slate-50 border border-slate-200', icon: 'Calendar' };
  };

  const dueDateInfo = formatDueDate(card.dueDate);
  const assignedMembers = getAssignedMembers();
  const completedTasks = card.checklist?.filter(item => item.completed).length || 0;
  const totalTasks = card.checklist?.length || 0;
  const checklistProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const priorityConfig = getPriorityConfig();

  // Generate consistent color for member avatars
  const getAvatarColor = (name) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-emerald-500 to-emerald-600',
      'from-purple-500 to-purple-600',
      'from-pink-500 to-pink-600',
      'from-indigo-500 to-indigo-600',
      'from-cyan-500 to-cyan-600',
    ];
    const hash = (name || '').split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div
      ref={ref}
      onClick={() => onClick(card)}
      className={`
        group relative bg-white/90 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden
        border border-white/60 dark:border-slate-700/60 shadow-sm
        border-l-4 ${priorityConfig.border}
        transition-all duration-300 ease-out
        ${canDrag ? 'cursor-pointer' : 'cursor-default'}
        ${isDragging 
          ? 'opacity-50 rotate-2 scale-105 shadow-2xl' 
          : canDrag 
            ? 'hover:shadow-[0_8px_25px_rgba(0,0,0,0.08)] hover:-translate-y-1 hover:border-blue-300/50 dark:hover:border-blue-500/40' 
            : ''
        }
      `}
    >
      {/* Subtle gradient overlay on hover */}
      {canDrag && (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-blue-50/0 group-hover:from-blue-50/50 dark:group-hover:from-slate-700/50 group-hover:to-transparent transition-all duration-200 pointer-events-none" />
      )}

      <div className="relative p-4">
        {/* Card Header */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-snug group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
            {card.title}
          </h4>

          {card.description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
              {card.description}
            </p>
          )}
        </div>

        {/* Labels */}
        {card.labels && card.labels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {card.labels.slice(0, 3).map((label) => {
              const labelColor = label.color || '#6B7280';
              const labelName = label.name || label.label || 'Label';
              return (
                <span
                  key={label.id || label.value || label.label || labelName}
                  className="px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wide"
                  style={{
                    backgroundColor: labelColor + '20',
                    color: labelColor,
                    border: `1px solid ${labelColor}40`
                  }}
                >
                  {labelName}
                </span>
              );
            })}
            {card.labels.length > 3 && (
              <span className="px-2 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 rounded-full">
                +{card.labels.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Checklist Progress Bar */}
        {totalTasks > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500 flex items-center gap-1">
                <Icon name="CheckSquare" size={12} />
                Checklist
              </span>
              <span className={`font-medium ${checklistProgress === 100 ? 'text-green-600' : 'text-slate-600'}`}>
                {completedTasks}/{totalTasks}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  checklistProgress === 100 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                }`}
                style={{ width: `${checklistProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Card Footer */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            {/* Priority Badge */}
            {card.priority && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md uppercase tracking-wide border ${priorityConfig.color}`}>
                {card.priority}
              </span>
            )}

            {/* Stats Row */}
            <div className="flex items-center gap-2 text-slate-400">
              {/* Comments Count */}
              {card.comments && card.comments.length > 0 && (
                <div className="flex items-center gap-0.5 text-xs">
                  <Icon name="MessageCircle" size={12} />
                  <span>{card.comments.length}</span>
                </div>
              )}

              {/* Attachments Count */}
              {card.attachments && card.attachments.length > 0 && (
                <div className="flex items-center gap-0.5 text-xs">
                  <Icon name="Paperclip" size={12} />
                  <span>{card.attachments.length}</span>
                </div>
              )}
            </div>
          </div>

          {/* Assigned Members */}
          {assignedMembers.length > 0 && (
            <div className="flex -space-x-1.5">
              {assignedMembers.slice(0, 3).map((member) => (
                <div
                  key={member.id}
                  className={`w-7 h-7 rounded-full border-2 border-white bg-gradient-to-br ${getAvatarColor(member.name)} flex items-center justify-center text-[10px] font-bold text-white shadow-sm`}
                  title={member.name}
                >
                  {(member.name || member.email || "User").split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              ))}
              {assignedMembers.length > 3 && (
                <div className="w-7 h-7 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 shadow-sm">
                  +{assignedMembers.length - 3}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Due Date */}
        {dueDateInfo && (
          <div className="mt-3">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg ${dueDateInfo.color}`}>
              <Icon name={dueDateInfo.icon} size={12} />
              {dueDateInfo.text}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard;